import React, { useEffect, useState } from 'react';
import { Copy, Link as LinkIcon, Download } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { PUBLIC_SITE_URL } from '../lib/referral';

/**
 * Family Referral Link — shown to every approved partner (Regional + Memorial).
 *
 * When a family signs up via this link, the backend stamps the partner's id
 * onto the family record (immutable) and the iter52 commission engine
 * attaches the right rows after Stripe webhook confirmation:
 *   - Regional Partner direct           → $100 to RP
 *   - Memorial Partner direct           → $100 to MP + $25 override to RP
 * Partners can never edit attribution after the fact — backend-enforced.
 *
 * Privacy: NO family PII is ever surfaced here. This is a write-only sharing
 * surface — the partner shares their link and waits.
 */
export function FamilyReferralLink({ code, accountType }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  // iter101 — ALWAYS the permanent production domain. Referral links and
  // QR posters get printed, emailed and texted; a preview-host link would
  // break for every recipient.
  const referralUrl = `${PUBLIC_SITE_URL}/register?ref=${code}`;

  useEffect(() => {
    if (!code) return;
    QRCode.toDataURL(referralUrl, {
      errorCorrectionLevel: 'H',
      margin: 2,
      width: 320,
      color: { dark: '#1A3626', light: '#FDFBF7' },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [code, referralUrl]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed — please copy manually.');
    }
  };

  const download = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `myleaf-referral-${code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const tierLabel =
    accountType === 'memorial_partner' ? 'Memorial Partner' : 'Regional Partner';

  return (
    <section
      className="relative overflow-hidden rounded-3xl border-2 border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--primary))] to-[#0F1F17] text-[#F4E5C2] px-8 py-9 mb-12 shadow-lg"
      data-testid="partner-family-referral-card"
    >
      <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-[hsl(var(--accent)/0.18)] blur-3xl pointer-events-none" />
      <div className="relative grid md:grid-cols-[1fr_240px] gap-8 items-start">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
            <LinkIcon size={14} /> Family referral link · {tierLabel}
          </div>
          <h2 className="font-serif text-3xl mt-2 !text-[#F4E5C2]">
            Refer a family — earn commission automatically.
          </h2>
          <p className="text-sm text-[#F4E5C2]/80 mt-3 max-w-2xl leading-relaxed">
            Share this link or QR code with families you serve. When they sign
            up through your link, our system attaches the commission to your
            account the moment they complete payment.
            {accountType === 'memorial_partner' ? (
              <span className="block mt-2 text-[#F4E5C2]/65">
                You earn <strong>$100</strong> per family enrolment, paid via
                Stripe Connect.
              </span>
            ) : (
              <span className="block mt-2 text-[#F4E5C2]/65">
                You earn <strong>$100</strong> per family you enrol directly,
                plus <strong>$25 override</strong> on every family enrolled by
                your Memorial Partners.
              </span>
            )}
          </p>

          <div className="mt-6 space-y-3">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--accent))] mb-1">
                Referral code
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <code
                  className="font-mono text-2xl tracking-[0.45em] bg-[#F4E5C2] text-[hsl(var(--primary))] px-5 py-3 rounded-lg shadow-md font-semibold"
                  data-testid="partner-referral-code-value"
                >
                  {code}
                </code>
                <button
                  onClick={() => copy(code, 'Code')}
                  className="ml-btn-outline-cream"
                  data-testid="partner-referral-code-copy"
                >
                  <Copy size={14} /> Copy code
                </button>
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--accent))] mb-1">
                Shareable link
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <code
                  className="font-mono text-xs sm:text-sm bg-[#F4E5C2] text-[hsl(var(--primary))] px-4 py-2.5 rounded-md break-all"
                  data-testid="partner-referral-link-value"
                >
                  {referralUrl}
                </code>
                <button
                  onClick={() => copy(referralUrl, 'Link')}
                  className="ml-btn-outline-cream"
                  data-testid="partner-referral-link-copy"
                >
                  <Copy size={14} /> Copy link
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center" data-testid="partner-referral-qr-wrapper">
          {qrDataUrl ? (
            <>
              <img
                src={qrDataUrl}
                alt="Referral QR code"
                className="w-44 h-44 rounded-lg bg-[#FDFBF7] p-2 shadow-md"
                data-testid="partner-referral-qr-image"
              />
              <button
                type="button"
                onClick={download}
                className="ml-btn-outline-cream mt-4"
                data-testid="partner-referral-qr-download"
              >
                <Download size={14} /> Download QR
              </button>
            </>
          ) : (
            <div className="w-44 h-44 rounded-lg bg-[#F4E5C2]/20 animate-pulse" />
          )}
        </div>
      </div>
    </section>
  );
}
